import type { CustomerSearchSourceHit } from "./customerSearchAccess";

export const CUSTOMER_CREATE_CLIENT_REVIEW_MESSAGE =
  "Please review the possible existing customers before creating a new record.";

export const CUSTOMER_CREATE_SERVER_REVIEW_MESSAGE =
  "Possible existing customers found. Review the matches before creating a new record.";

const REVIEW_MESSAGE_RE =
  /possible existing customers|review the (possible existing |latest )?match|potential existing customer|select the correct account instead of creating a duplicate|creation is blocked pending manual review/i;

export type CustomerCreateGuardDecisionForReview =
  | "reuse_existing"
  | "require_selection"
  | "blocked_conflict"
  | "require_explicit_confirmation"
  | "allow_new";

export type CustomerDuplicateCheckResult<TCandidate = CustomerSearchSourceHit> = {
  candidates: TCandidate[];
  /** True when create would fail or silently reuse unless staff review/override. */
  requiresReview: boolean;
};

const GUARD_DECISIONS_REQUIRING_REVIEW: ReadonlySet<string> = new Set([
  "reuse_existing",
  "require_selection",
  "blocked_conflict",
  "require_explicit_confirmation",
]);

export function customerCreationRequiresDuplicateReview(input: {
  unreviewedStrongCount: number;
  guardDecision: CustomerCreateGuardDecisionForReview | string;
}): boolean {
  if (input.unreviewedStrongCount > 0) return true;
  return GUARD_DECISIONS_REQUIRING_REVIEW.has(input.guardDecision);
}

export function isCustomerCreateDuplicateReviewMessage(message: string | null | undefined): boolean {
  return REVIEW_MESSAGE_RE.test(String(message ?? ""));
}

export function customerDuplicateCheckCandidates<T>(
  result: CustomerDuplicateCheckResult<T> | T[] | null | undefined,
): T[] {
  if (!result) return [];
  if (Array.isArray(result)) return result;
  return Array.isArray(result.candidates) ? result.candidates : [];
}

export function customerDuplicateCheckRequiresReview(
  result: CustomerDuplicateCheckResult<unknown> | unknown[] | null | undefined,
): boolean {
  if (!result || Array.isArray(result)) return false;
  return Boolean(result.requiresReview);
}

export function toCustomerDuplicatePickerRow(d: {
  id: number;
  name: string;
  contact?: string | null;
}): { id: number; name: string; contact: string | null } {
  return { id: d.id, name: d.name, contact: d.contact ?? null };
}
